/**
 * Helpers for turning stored rich/escaped text into readable plain text.
 *
 * Editor content is sanitized on save, which HTML-encodes bare characters like
 * `&` into `&amp;`. That is correct for values rendered via
 * dangerouslySetInnerHTML (the browser decodes them), but wrong for values
 * rendered as React text nodes or <meta> content, where the entity shows
 * literally. Decoding once here undoes exactly one sanitize/encode pass.
 */

const NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
};

/** Decode the HTML entities a sanitizer emits (named commons + numeric). */
export function decodeEntities(text: string | null | undefined): string {
  if (!text || !text.includes("&")) return text || "";
  return text.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z][a-z0-9]*);/gi, (match, body: string) => {
    if (body[0] === "#") {
      const code = body[1].toLowerCase() === "x"
        ? parseInt(body.slice(2), 16)
        : parseInt(body.slice(1), 10);
      return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : match;
    }
    const named = NAMED_ENTITIES[body.toLowerCase()];
    return named ?? match;
  });
}

/**
 * Strip tags and decode entities — a stored HTML string as displayable text.
 * Accepts `unknown` defensively: a localized attribute can round-trip through
 * automation layers (e.g. PHP, which cannot distinguish a JSON `{}` from
 * `[]`) as something other than a string. Malformed input renders as no text
 * instead of throwing (board issue 18: `html.replace is not a function`).
 */
export function toPlainText(html: unknown): string {
  if (typeof html !== "string") return "";
  return decodeEntities(html.replace(/<[^>]*>/g, ""));
}
